import {
  AGE_BAND_OPTIONS,
  CASE_TYPE_OPTIONS,
  HILLOS,
  LEARNER_GRADE_OPTIONS,
  REFERRAL_REASON_OPTIONS,
  isAlreadyAdmitted,
  isHilloNumber,
  procedureLabel,
} from '@iculogbook/shared';
import type { CaseType, LearnerGrade, ReferralReason } from '@iculogbook/shared';

import {
  Button,
  ChipGroup,
  Disclosure,
  Field,
  NO_IDENTIFIABLE_INFORMATION,
  TextArea,
  TextInput,
} from '../../components/index.ts';
import { CapabilityLevelField } from './CapabilityLevelField.tsx';
import { CareLevelField } from './CareLevelField.tsx';
import { OccurredAtField } from './OccurredAtField.tsx';
import type { FormErrors } from './ProcedureForm.tsx';
import type { CaseFormValues, NestedProcedureDraft } from './caseForm.ts';
import { missingCaseFields } from './caseForm.ts';

const CASE_TYPE_CHIPS = CASE_TYPE_OPTIONS.map((option) => ({
  value: option.key,
  label: option.label,
}));

const TITLE_MAX_LENGTH = 140;

type CaseFormProps = {
  values: CaseFormValues;
  onChange: (values: CaseFormValues) => void;
  procedures: readonly NestedProcedureDraft[];
  onAddProcedure: () => void;
  onEditProcedure: (draftId: string) => void;
  onSave: () => void;
  /** Saves, then opens a new form prefilled from what was just saved. */
  onSaveAndDuplicate: () => void;
  onDelete?: (() => void) | undefined;
  saving: boolean;
  editing: boolean;
  errors: FormErrors;
};

/**
 * The case form. A case is an admission, a ward review, a phone call, or a
 * notable episode with a patient already on the unit — one clinical event,
 * which may have produced several procedures.
 *
 * Those procedures are listed inside this form and saved with it, because
 * making a trainee record the same date, age band and capability level four
 * times for one admission is the largest single piece of friction this app can
 * remove. See the product decisions in PRD.md.
 */
export function CaseForm({
  values,
  onChange,
  procedures,
  onAddProcedure,
  onEditProcedure,
  onSave,
  onSaveAndDuplicate,
  onDelete,
  saving,
  editing,
  errors,
}: CaseFormProps) {
  function set<K extends keyof CaseFormValues>(key: K, value: CaseFormValues[K]): void {
    onChange({ ...values, [key]: value });
  }

  const incomplete = procedures.filter(
    (draft) => draft.values.ageBand === null || draft.values.capabilityLevel === null,
  ).length;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
      className="flex flex-col gap-4"
      noValidate
    >
      {/*
        Chips rather than a segmented control. There are four case types now
        and one of them is "Notable ICU case"; four word-labels of that length
        cannot fit a segmented control on a 320px phone even at `compact`, and
        this codebase's most reliable layout bug is a flex row that widens its
        container rather than shrinking. Chips wrap by construction, so the
        question does not arise. See PROGRESS.md.

        Case type is required, so a deselecting tap — which ChipGroup allows,
        every other chip field here being optional — is ignored rather than
        clearing the field.
      */}
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-text">Case type</span>
        <ChipGroup
          label="Case type"
          options={CASE_TYPE_CHIPS}
          value={values.caseType}
          onChange={(next: CaseType | null) => {
            if (next !== null) set('caseType', next);
          }}
        />
      </div>

      <Field
        label="What happened"
        help={NO_IDENTIFIABLE_INFORMATION}
        error={errors['title']}
        required
      >
        <TextInput
          value={values.title}
          onChange={(event) => set('title', event.target.value)}
          maxLength={TITLE_MAX_LENGTH}
          placeholder="Septic shock, urinary source"
          autoFocus
          enterKeyHint="done"
        />
      </Field>

      <CapabilityLevelField
        value={values.capabilityLevel}
        onChange={(level) => set('capabilityLevel', level)}
        error={errors['capabilityLevel']}
      />

      <CareLevelField value={values.careLevel} onChange={(level) => set('careLevel', level)} />

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-text">Age band</span>
        <ChipGroup
          label="Age band"
          options={AGE_BAND_OPTIONS.map((option) => ({ value: option.key, label: option.label }))}
          value={values.ageBand}
          onChange={(next) => set('ageBand', next)}
        />
        {errors['ageBand'] === undefined ? null : (
          <p role="alert" className="text-xs text-danger">
            {errors['ageBand']}
          </p>
        )}
      </div>

      <OccurredAtField
        value={values.occurredAt}
        onChange={(next) => set('occurredAt', next)}
        error={errors['occurredAt']}
      />

      <section className="flex flex-col gap-2 rounded-md border border-line bg-surface p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-medium text-text">Procedures</h2>
          <span className="text-xs text-faint">
            {procedures.length === 0 ? 'None yet' : `${String(procedures.length)} in this case`}
          </span>
        </div>

        {procedures.length === 0 ? null : (
          <ul className="flex flex-col gap-1">
            {procedures.map((draft) => (
              <li key={draft.draftId}>
                <button
                  type="button"
                  onClick={() => onEditProcedure(draft.draftId)}
                  className="flex min-h-touch w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left hover:bg-surface-raised"
                >
                  <span className="text-base text-text">
                    {procedureLabel(draft.values.procedureKey)}
                    {draft.values.variant === null ? '' : ` · ${draft.values.variant}`}
                  </span>
                  <span className="shrink-0 text-xs text-faint">
                    {draft.values.capabilityLevel === null
                      ? 'Incomplete'
                      : `Level ${String(draft.values.capabilityLevel)}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <Button variant="secondary" onClick={onAddProcedure}>
          ＋ Add procedure
        </Button>

        {incomplete === 0 ? null : (
          <p role="alert" className="text-xs text-danger">
            {incomplete === 1
              ? 'One procedure still needs an age band or capability level.'
              : `${String(incomplete)} procedures still need an age band or capability level.`}
          </p>
        )}
      </section>

      {/*
        The same stored `ReferralReason` keys under two labels. Nobody
        referred a patient who is already on the unit, so calling the field
        "reasons for referral" there would be wrong; calling the problems by
        their own name keeps the case-mix aggregation, the PDF and the export
        whole, which hiding the field for this case type would not.
      */}
      <Disclosure
        title={isAlreadyAdmitted(values.caseType) ? 'Active problems' : 'Reasons for referral'}
        hint="Optional. Several can apply."
        badge={
          values.reasonsForReferral.length === 0
            ? null
            : `${String(values.reasonsForReferral.length)} chosen`
        }
      >
        <ChipGroup
          label={isAlreadyAdmitted(values.caseType) ? 'Active problems' : 'Reasons for referral'}
          multiple
          options={REFERRAL_REASON_OPTIONS.map((option) => ({
            value: option.key,
            label: option.label,
          }))}
          value={values.reasonsForReferral}
          onChange={(next) => set('reasonsForReferral', next as ReferralReason[])}
        />
      </Disclosure>

      {/*
        Teaching during a case, rather than as its own EDT entry: it happened
        inside this clinical episode, and a separate entry would count one
        afternoon twice. Choosing a grade is what "I was teaching" means —
        there is no separate yes/no to keep in step with it. Teaching maps to
        HiLLO 4, but the trainee tags that themselves; the form no longer
        proposes HiLLOs. See CURRICULUM.md.
      */}
      <Disclosure
        title="Teaching or supervising"
        hint="Optional. Who you taught or supervised during this case."
        badge={
          values.teachingGrades.length === 0
            ? null
            : `${String(values.teachingGrades.length)} chosen`
        }
      >
        <ChipGroup
          label="Who you taught or supervised"
          multiple
          options={LEARNER_GRADE_OPTIONS.map((option) => ({
            value: option.key,
            label: option.label,
          }))}
          value={values.teachingGrades}
          onChange={(next) => set('teachingGrades', next as LearnerGrade[])}
        />
      </Disclosure>

      <Disclosure
        title="Note"
        hint="Anything worth remembering about this case"
        badge={values.note.trim() === '' ? null : 'Written'}
      >
        <Field label="Note" help={NO_IDENTIFIABLE_INFORMATION} error={errors['note']}>
          <TextArea
            value={values.note}
            onChange={(event) => set('note', event.target.value)}
            maxLength={500}
            rows={3}
          />
        </Field>
      </Disclosure>

      <Disclosure
        title="Curriculum"
        hint="Optional HiLLO tagging. Nothing is stored until you tap."
        badge={values.hillos.length === 0 ? null : `${String(values.hillos.length)} tagged`}
      >
        <ChipGroup
          label="High Level Learning Outcomes"
          multiple
          options={HILLOS.map((hillo) => ({
            value: String(hillo.number),
            label: `${String(hillo.number)}. ${hillo.shortLabel}`,
          }))}
          value={values.hillos.map(String)}
          onChange={(next) => set('hillos', next.map(Number).filter(isHilloNumber))}
        />
      </Disclosure>

      <Button type="submit" variant="primary" block disabled={saving} className="h-14">
        {editing ? 'Save changes' : 'Save case'}
        {procedures.length === 0
          ? ''
          : ` and ${String(procedures.length)} procedure${procedures.length === 1 ? '' : 's'}`}
      </Button>

      <Button
        variant="secondary"
        block
        disabled={saving}
        onClick={onSaveAndDuplicate}
        className="h-14"
      >
        {editing ? 'Save changes and duplicate' : 'Save and duplicate'}
      </Button>

      {onDelete === undefined ? null : (
        <Button variant="ghost" onClick={onDelete} className="text-danger hover:text-danger-hover">
          Delete this case
        </Button>
      )}

      {/* Named here so nothing saves while a required choice is still missing. */}
      <p className="sr-only" aria-live="polite">
        {missingCaseFields(values).length === 0
          ? 'Ready to save'
          : 'Some required fields are empty'}
      </p>
    </form>
  );
}
